import { ComponentType, LazyExoticComponent, lazy } from "react";
import * as Sentry from "@sentry/react";

const CHUNK_RELOAD_KEY = "tender-chunk-reload-attempted";

const CHUNK_ERROR_PATTERNS = [
  /Failed to fetch dynamically imported module/,
  /Importing a module script failed/,
  /error loading dynamically imported module/,
];

export const isChunkLoadError = (error: unknown): boolean => {
  if (!(error instanceof Error)) return false;
  return CHUNK_ERROR_PATTERNS.some((pattern) => pattern.test(error.message));
};

const hasReloadFlag = (): boolean => {
  try {
    return sessionStorage.getItem(CHUNK_RELOAD_KEY) !== null;
  } catch {
    // No storage (private mode, etc.) — behave as if guarded to avoid reload loops.
    return true;
  }
};

const setReloadFlag = (): boolean => {
  try {
    sessionStorage.setItem(CHUNK_RELOAD_KEY, "1");
    return true;
  } catch {
    return false;
  }
};

// Dedupe captures per error reference — React StrictMode double-renders in dev,
// which would otherwise emit a spurious `persistent` event on the second render.
const capturedErrors = new WeakSet<object>();

const captureOnce = (error: unknown, tag: "recovered" | "persistent"): void => {
  if (typeof error !== "object" || error === null) return;
  if (capturedErrors.has(error)) return;
  capturedErrors.add(error);
  Sentry.captureException(error, { tags: { staleChunk: tag } });
};

export const attemptReloadOnChunkError = (error: unknown): boolean => {
  if (!isChunkLoadError(error)) return false;
  if (hasReloadFlag()) {
    captureOnce(error, "persistent");
    return false;
  }
  if (!setReloadFlag()) return false;
  captureOnce(error, "recovered");
  window.location.reload();
  return true;
};

export const createReloadingLoader = <T>(
  loader: () => Promise<{ default: T }>,
) => {
  return async (): Promise<{ default: T }> => {
    try {
      return await loader();
    } catch (error) {
      if (attemptReloadOnChunkError(error)) {
        return new Promise<{ default: T }>(() => {});
      }
      throw error;
    }
  };
};

export const lazyWithReload = <T extends ComponentType<any>>( // eslint-disable-line @typescript-eslint/no-explicit-any
  loader: () => Promise<{ default: T }>,
): LazyExoticComponent<T> => lazy(createReloadingLoader(loader));
